import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import {
  concat,
  concatMap,
  exhaustMap,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  take,
} from 'rxjs';

import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { EffectsBase, withCacheGate } from '@ps/core-data-access';
import { CACHE_TTL, markCached } from '@ps/utils';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';

import { NotificationService } from '../services/notification.service';
import { NOTIFICATION_LIMIT_DEFAULT } from '../api/notification-api.constants';
import {
  NotificationActions,
  NotificationApiActions,
} from './notification.actions';
import {
  selectNotificationsCachedAt,
  selectUnseenCountCachedAt,
} from './notification.selectors';

@Injectable()
export class NotificationEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _notificationService = inject(NotificationService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);

  /**
   * Declared before any effect that reads it: `createEffect` builds its
   * pipeline at field initialization.
   */
  private readonly _enabled = computed(
    () =>
      this._authSelectors.isAuthenticated() &&
      (this._authSelectors.isOperator() ||
        !!this._userSettingsSelectors.buyerSettings().enableNotificationCenter),
  );

  private readonly _enabled$ = toObservable(this._enabled);

  /**
   * Refresh on every completed navigation, initial page load included.
   * Buyer settings may resolve after the first navigation (fresh login), so
   * wait for the gate to open instead of sampling it synchronously. A newer
   * navigation cancels a pending wait.
   */
  public readonly refreshOnNavigation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(routerNavigatedAction),
      switchMap(() => this._enabled$.pipe(filter(Boolean), take(1))),
      map(() => NotificationActions.loadUnseenCount()),
    ),
  );

  /**
   * Single fan-out for every refresh source. The gate here keeps flag-off
   * tenants and logged-out sessions at zero HTTP.
   */
  public readonly refresh$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.refresh),
      filter(() => this._enabled()),
      mergeMap(() => [
        NotificationActions.loadUnseenCount(),
        NotificationActions.loadNotifications(),
      ]),
    ),
  );

  public readonly loadNotifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.loadNotifications),
      filter(() => this._enabled()),
      withCacheGate(
        this._store.select(selectNotificationsCachedAt),
        CACHE_TTL.SHORT,
      ),
      switchMap(() =>
        concat(
          of(NotificationApiActions.loadNotificationsStarted()),
          this._notificationService
            .notifications(NOTIFICATION_LIMIT_DEFAULT)
            .pipe(
              map((notifications) =>
                NotificationApiActions.loadNotificationsSuccess({
                  notifications,
                  cachedAt: markCached(),
                }),
              ),
              this.psCatchError((error) =>
                of(
                  NotificationApiActions.loadNotificationsFailure({
                    error: error.message,
                  }),
                ),
              ),
            ),
        ),
      ),
    ),
  );

  public readonly loadUnseenCount$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.loadUnseenCount),
      filter(() => this._enabled()),
      withCacheGate(
        this._store.select(selectUnseenCountCachedAt),
        CACHE_TTL.SHORT,
      ),
      switchMap(() =>
        this._notificationService.unseenCount().pipe(
          map((unseenCount) =>
            NotificationApiActions.loadUnseenCountSuccess({
              unseenCount,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError((error) =>
            of(
              NotificationApiActions.loadUnseenCountFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly markAllViewed$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.markAllViewed),
      exhaustMap(() =>
        this._notificationService.viewAll().pipe(
          map((changed) =>
            NotificationApiActions.markAllViewedSuccess({ changed }),
          ),
          this.psCatchError(
            (error) =>
              of(
                NotificationApiActions.markAllViewedFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly markViewed$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.markViewed),
      concatMap(({ notificationId, viewed }) =>
        this._notificationService.markViewed(notificationId, viewed).pipe(
          map((notification) =>
            NotificationApiActions.markViewedSuccess({ notification }),
          ),
          this.psCatchError(
            (error) =>
              of(
                NotificationApiActions.markViewedFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  /**
   * The list is capped by `limit`, so the server owns the unseen total.
   * Re-sync count and list after every viewed mutation.
   */
  public readonly refreshAfterViewedMutation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        NotificationApiActions.markAllViewedSuccess,
        NotificationApiActions.markViewedSuccess,
      ),
      map(() => NotificationActions.refresh()),
    ),
  );
}
